import {
	KeyboardEvent,
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import {
	KeyBoardListNavigationContext,
	useKeyboardListNavigation,
	useKeyboardListNavigationItem
} from "client/Hooks";
import { Key } from "client/Misc/commonTypes";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { IconButton } from "../IconButton/IconButton";
import { localize } from "client_legacy/ndaw/culture";
import { ActionListItem } from "../ActionList/ActionListItem";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import { IconColor } from "../Icon/Icon";
import { never } from "client_legacy/ndaw/errors";
import {
	delay,
	ensureNotNil,
	generateTrackingId
} from "client/Misc/utils";

export enum SkinTone {
	Light = 0,
	MediumLight = 1,
	Medium = 2,
	MediumDark = 3,
	Dark = 4,
}

type SkinToneSelectorProps = {
	value: SkinTone | null;
	onChange(value: SkinTone | null): void;
};

export const SkinToneSelector = (props: SkinToneSelectorProps): ReactElement => {
	const {
		value,
		onChange
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const buttonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const iconColor = useMemo(
		(): IconColor => getIconColor(value),
		[value]
	);

	const focusButtonHandler = useCallback(
		(): void => {
			buttonRef.current
				?.focus();
		},
		[]
	);

	const selectToneHandler = useCallback(
		(nextValue: SkinTone | null): void => {
			setIsOpen(false);
			onChange(nextValue);
			focusButtonHandler();
		},
		[focusButtonHandler, onChange]
	);

	const cancelHandler = useCallback(
		(): void => {
			setIsOpen(false);
			focusButtonHandler();
		},
		[focusButtonHandler]
	);

	const {
		contextData,
		focusFirstItemHandler,
		focusItemAtIndexHandler
	} = useKeyboardListNavigation({
		onFocusInput: focusButtonHandler,
		onCancel: cancelHandler,
		shouldCancelDirectlyFromItems: true
	});

	const selectedOptionIndex = useMemo(
		(): number => skinToneOptions.findIndex(
			option => option.value === value
		),
		[value]
	);

	const buttonKeyDownHandler = useCallback(
		async (args: KeyboardEvent<HTMLElement>): Promise<void> => {
			const key: Key | null = tryResolveKeyboardKey(args);

			switch (key) {
				case Key.ArrowDown:
					args.preventDefault();

					if (isOpen) {
						focusFirstItemHandler();
						return;
					}

					setIsOpen(true);

					// Wait for the dropdown to open, then focus the appropriate item.
					await delay(1);

					if (selectedOptionIndex !== -1) {
						focusItemAtIndexHandler(selectedOptionIndex);
					} else {
						focusFirstItemHandler();
					}

					return;
				case Key.Enter:
				case Key.Space:
					args.preventDefault();

					if (isOpen) {
						setIsOpen(false);
						return;
					}

					args.stopPropagation();
					setIsOpen(true);

					// Wait for the dropdown to open, then focus the appropriate item.
					await delay(1);

					if (selectedOptionIndex !== -1) {
						focusItemAtIndexHandler(selectedOptionIndex);
					} else {
						focusFirstItemHandler();
					}
					return;
				case Key.Escape:
				case Key.ArrowUp:
					args.preventDefault();

					setIsOpen(false);
					return;
				case Key.Tab:
					setIsOpen(false);
					return;
				default:
					return;
			}
		},
		[
			focusFirstItemHandler,
			focusItemAtIndexHandler,
			selectedOptionIndex,
			isOpen
		]
	);

	return (
		<>
			<IconButton
				ref={buttonRef}
				kind="transparent-colored"
				trackingId="skin-tone-selector"
				tooltip={localize("_SkinTone")}
				onClick={(): void => setIsOpen(open => !open)}
				onKeyDown={buttonKeyDownHandler}
				icon={{
					kind: "font",
					name: "circle",
					color: iconColor
				}}
			/>
			{isOpen && (
				<DropOverActionList
					target={ensureNotNil(buttonRef.current)}
					onClose={(): void => setIsOpen(false)}
					trackingId="skin-tone-selector__dropover"
					menuTitle={localize("_SkinTone")}
					targetAlignment="br"
					contentAlignment="tr"
				>
					<KeyBoardListNavigationContext.Provider value={contextData}>
						{skinToneOptions.map(
							(option, index): ReactElement => (
								<SkinToneListItem
									key={option.text}
									itemIndex={index}
									option={option}
									isSelected={value === option.value}
									onSelect={selectToneHandler}
								/>
							)
						)}
					</KeyBoardListNavigationContext.Provider>
				</DropOverActionList>
			)}
		</>
	);
};
SkinToneSelector.displayName = "SkinToneSelector";

type SkinToneOption = {
	value: SkinTone | null;
	text: string;
};

const skinToneOptions: readonly SkinToneOption[] = [
	{
		text: localize("_Default"),
		value: null
	},
	{
		text: localize("_Light"),
		value: SkinTone.Light
	},
	{
		text: localize("_MediumLight"),
		value: SkinTone.MediumLight
	},
	{
		text: localize("_Medium"),
		value: SkinTone.Medium
	},
	{
		text: localize("_MediumDark"),
		value: SkinTone.MediumDark
	},
	{
		text: localize("_Dark"),
		value: SkinTone.Dark
	}
];

type SkinToneListItemProps = {
	itemIndex: number;
	option: SkinToneOption;
	isSelected: boolean;
	onSelect(value: SkinTone | null): void;
};

const SkinToneListItem = (props: SkinToneListItemProps): ReactElement => {
	const {
		itemIndex,
		option,
		isSelected,
		onSelect
	} = props;

	const color = useMemo(
		(): IconColor => {
			return getIconColor(option.value);
		},
		[option.value]
	);

	const executeHandler = useCallback(
		(): void => {
			onSelect(option.value);
		},
		[onSelect, option.value]
	);

	const {
		ref,
		onKeyDown
	} = useKeyboardListNavigationItem({
		itemIndex,
		executeKey: [Key.Enter, Key.Space],
		shouldCancelOnTab: true,
		onExecute: executeHandler
	});

	const keyDownHandler = useCallback(
		(args: KeyboardEvent<HTMLLIElement>): void => {
			args.stopPropagation();
			onKeyDown(args);
		},
		[onKeyDown]
	);

	return (
		<ActionListItem
			ref={ref}
			tabIndex={-1}
			role="option"
			icon={{
				kind: "font",
				name: "circle",
				color
			}}
			rightIcon={isSelected ? "check" : undefined}
			isActive={isSelected}
			text={option.text}
			onClick={(): void => onSelect(option.value)}
			onKeyDown={keyDownHandler}
			trackingId={generateTrackingId("skin-tone", `|${option.value}`)}
		/>
	);
};
SkinToneListItem.displayName = "SkinToneListItem";

function getIconColor(skinTone: SkinTone | null): IconColor {
	if (skinTone === null) {
		return "default-skin-color";
	}

	switch (skinTone) {
		case SkinTone.Light:
			return "light-skin-color";
		case SkinTone.MediumLight:
			return "medium-light-skin-color";
		case SkinTone.Medium:
			return "medium-skin-color";
		case SkinTone.MediumDark:
			return "medium-dark-skin-color";
		case SkinTone.Dark:
			return "dark-skin-color";
		default:
			return never(skinTone);
	}
}